import { ProgramWithDetail } from "@humanex/interfaces";
import { AppSidebar } from "@humanex/ui/components";
import { cn } from "@humanex/ui/libs";
import { Link, useLocation, useMatchRoute, useRouterState } from "@tanstack/react-router";
import { BookOpen, ChevronLeft, ClipboardCheck, HelpCircle, LayoutDashboard, Users } from "lucide-react";

import { Logo } from "@/components";

const ORG_NAV_ITEMS = [
  { to: "/org/$orgId", label: "Dashboard", icon: LayoutDashboard, exact: true },
  {
    to: "/org/$orgId/program",
    label: "Chương trình",
    icon: BookOpen,
    exact: false
  },
  { to: "/org/$orgId/members", label: "Thành viên", icon: Users, exact: false }
] as const;

const PROGRAM_NAV_ITEMS = [
  {
    to: "/org/$orgId/program/$programId",
    label: "Tổng quan",
    icon: LayoutDashboard,
    exact: true
  },
  {
    to: "/org/$orgId/program/$programId/applicants",
    label: "Ứng viên",
    icon: Users,
    exact: false
  },
  {
    to: "/org/$orgId/program/$programId/questions",
    label: "Câu hỏi bổ sung",
    icon: HelpCircle,
    exact: false
  },
  {
    to: "/org/$orgId/program/$programId/revisions",
    label: "Xét duyệt",
    icon: ClipboardCheck,
    exact: true
  }
] as const;

interface SidebarProps {
  orgId?: string;
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ orgId, open, onClose }: SidebarProps) {
  const { pathname: currentPath } = useLocation();
  const match = useMatchRoute();
  const routerState = useRouterState();

  const programMatch = currentPath.match(/\/org\/[^/]+\/program\/([^/]+)/);
  const programId = programMatch?.[1] ?? null;

  const programContext = routerState.matches.find(
    (m): m is typeof m & { context: { program: ProgramWithDetail } } =>
      !!m.context && typeof m.context === "object" && "program" in m.context
  )?.context;
  const currentProgram = programContext?.program ?? null;

  if (!orgId) return null;

  return (
    <AppSidebar
      open={open}
      onClose={onClose}
      collapseBelow="md"
      className="w-56 md:w-14 lg:w-56 border border-white bg-white/95 md:bg-white/80 rounded-r-3xl md:rounded-3xl shadow-sm"
    >
      <div className="py-4 border-b border-primary-100 flex flex-col justify-center gap-2 items-center">
        <Logo className="h-14 w-40 md:h-9 md:w-9 lg:h-14 lg:w-40" />
        <span className="md:hidden lg:block text-responsive-lg font-medium text-primary">TENANT PORTAL</span>
      </div>
      {programId !== null ? (
        <>
          <div className="border-b border-primary-100 px-2 py-1">
            <Link
              to="/org/$orgId/program"
              params={{ orgId }}
              onClick={onClose}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded text-responsive-md text-primary font-medium",
                "hover:bg-primary-50 md:justify-center lg:justify-start"
              )}
            >
              <ChevronLeft className="w-5 h-5 flex-shrink-0" />
              <p className="flex-1 truncate text-center md:hidden lg:block">
                {currentProgram?.title ?? "Chương trình"}
              </p>
              <ChevronLeft className="w-5 h-5 opacity-0 md:hidden lg:block" />
            </Link>
          </div>
          <nav className="flex-1 py-4 space-y-0.5 px-2">
            {PROGRAM_NAV_ITEMS.map((item) => {
              const isActive = match({ to: item.to });
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  params={{ orgId, programId }}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded text-responsive-md transition-colors",
                    "md:justify-center lg:justify-start",
                    isActive
                      ? "bg-primary-100/80 text-primary font-medium"
                      : "text-slate-400 hover:bg-primary-50"
                  )}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="md:hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </>
      ) : (
        <nav className="flex-1 py-4 space-y-0.5 px-2">
          {ORG_NAV_ITEMS.map((item) => {
            const isActive = match({ to: item.to });
            return (
              <Link
                key={item.to}
                to={item.to}
                params={{ orgId }}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded text-responsive-md transition-colors",
                  "md:justify-center lg:justify-start",
                  isActive
                    ? "bg-primary-100/80 text-primary font-medium"
                    : "text-slate-400 hover:bg-primary-50"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="md:hidden lg:inline">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </AppSidebar>
  );
}
